import { ThreeCanvas } from "@remotion/three";
import { zColor } from "@remotion/zod-types";
import React, { useMemo } from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { z } from "zod";
import {
  CAMERA_DISTANCE,
  getPhoneLayout,
  getPhoneOffscreenOffset,
  getPhoneSettledOffset,
  getWhiteboardLayout,
} from "./helpers/layout";
import {
  PHONE_SLIDE_IN_END_FRAME,
  PHONE_SLIDE_IN_START_FRAME,
} from "./helpers/timeline";
import { Phone } from "./Phone";
import { ScreenshotOverlay } from "./ScreenshotOverlay";
import { TimerPopUp } from "./TimerPopUp";
import { Whiteboard } from "./Whiteboard";

// Photo-to-timer parsing scene — animated 3D phone + whiteboard + timer
// over a transparent canvas. Rendered as a PNG sequence, encoded to an
// animated WebP, and composited inside Flutter against a Flutter-drawn
// photo background and Flutter-drawn text on the auth landing.

const SCREENSHOT_ASPECT_RATIO = 1179 / 2556;

const lerpVec3 = (
  a: [number, number, number],
  b: [number, number, number],
  t: number,
): [number, number, number] => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];

export const photoParsingSceneSchema = z.object({
  phoneColor: zColor(),
});

type PhotoParsingSceneSchemaType = z.infer<typeof photoParsingSceneSchema>;

export const PhotoParsingScene: React.FC<
  {
    readonly baseScale: number;
  } & PhotoParsingSceneSchemaType
> = ({ baseScale, phoneColor }) => {
  const { width, height } = useVideoConfig();
  const frame = useCurrentFrame();

  const layout = useMemo(
    () => getPhoneLayout(SCREENSHOT_ASPECT_RATIO, baseScale),
    [baseScale],
  );

  const whiteboardLayout = useMemo(
    () => getWhiteboardLayout(baseScale),
    [baseScale],
  );

  const phoneSlideProgress = interpolate(
    frame,
    [PHONE_SLIDE_IN_START_FRAME, PHONE_SLIDE_IN_END_FRAME],
    [0, 1],
    {
      easing: Easing.out(Easing.cubic),
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    },
  );

  const phoneOffscreen = useMemo(
    () => getPhoneOffscreenOffset(baseScale),
    [baseScale],
  );
  const phoneSettled = useMemo(
    () => getPhoneSettledOffset(baseScale),
    [baseScale],
  );
  const timerTarget = useMemo(
    (): [number, number, number] => [
      baseScale * -0.5,
      baseScale * 0.4,
      baseScale * 0.3,
    ],
    [baseScale],
  );
  const phoneWorldOffset = useMemo(
    () => lerpVec3(phoneOffscreen, phoneSettled, phoneSlideProgress),
    [phoneOffscreen, phoneSettled, phoneSlideProgress],
  );

  return (
    <AbsoluteFill style={{ backgroundColor: "transparent" }}>
      <ThreeCanvas
        linear
        width={width}
        height={height}
        camera={{
          position: [0, CAMERA_DISTANCE * 0.85, CAMERA_DISTANCE * 0.95],
          fov: 45,
          near: 0.1,
          far: Math.max(50, CAMERA_DISTANCE * 4),
        }}
        onCreated={(state) => {
          state.camera.lookAt(0, 0, 0);
        }}
      >
        <ambientLight intensity={1.2} color={0xffffff} />
        <directionalLight position={[3, 5, 4]} intensity={0.9} />
        <pointLight position={[-3, 4, 2]} intensity={0.4} />
        <Whiteboard
          whiteboardLayout={whiteboardLayout}
          baseScale={baseScale}
        />
        <Phone
          phoneColor={phoneColor}
          phoneLayout={layout}
          worldOffset={phoneWorldOffset}
        />
        <TimerPopUp
          phoneLayout={layout}
          worldOffset={phoneSettled}
          targetPosition={timerTarget}
        />
      </ThreeCanvas>
      <ScreenshotOverlay />
    </AbsoluteFill>
  );
};
